import React from "react";
import { Grid, Typography, Table, TableBody, TableCell, TableHead, TableRow, Box } from "@mui/material";
import { Bucket } from "../../../../api/config/apiConfig";
import DynamicCheckbox from "../../../molecules/DynamicCheckbox";
import Input from "../../../atoms/Input"; // Kept for consistency if needed, but likely replaced by display text

const WoodenLiftPageDownload = ({ formContent }) => {
  
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    const ordinalSuffix = getOrdinalSuffix(day);
    return `${day}${ordinalSuffix} ${month}, ${year}`;
  };

  const cellStyle = {
    padding: "4px 8px",
    fontSize: "12px",
    border: "1px solid #e0e0e0"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: "bold",
    backgroundColor: "#f5f5f5"
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    marginRight: "8px"
  };

  const valueStyle = {
    fontSize: "12px"
  };

  const checklistItemStyle = {
    fontSize: "12px",
    marginBottom: "4px"
  };

  return (
    <div className="mx-4" style={{ fontFamily: "Arial, sans-serif" }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '18px' }}>
          Wooden Lift Van Quality Check Report
        </Typography>
      </Box>

      {/* Customer Details */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={labelStyle}>Customer Name:</Typography>
              <Typography sx={valueStyle}>
                {formContent?.customerName || "-"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
             <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={labelStyle}>File Ref:</Typography>
              <Typography sx={valueStyle}>
                {formContent?.fileRef || "-"}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Inventory Table */}
      <Box sx={{ mb: 3 }}>
        <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle} width="15%">No. of Wooden lift vans</TableCell>
              <TableCell sx={headerCellStyle} width="40%">Lift van or Box Sizes IN/OUT</TableCell>
              <TableCell sx={headerCellStyle} width="20%">Tare weight</TableCell>
              <TableCell sx={headerCellStyle} width="25%">Manufacturer Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {formContent?.dataRows?.length > 0 ? (
              formContent.dataRows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={cellStyle}>{index + 1}</TableCell>
                  <TableCell sx={cellStyle}>{row.liftVanBoxSizes || "-"}</TableCell>
                  <TableCell sx={cellStyle}>{row.tareWt ? `${row.tareWt} KGS` : "-"}</TableCell>
                  <TableCell sx={cellStyle}>{formatDate(row.date)}</TableCell>
                </TableRow>
              ))
            ) : (
                <TableRow>
                  <TableCell colSpan={4} sx={{ ...cellStyle, textAlign: 'center', py: 2 }}>
                     No Records to Show
                  </TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Checklist */}
      <Box sx={{ mb: 3 }}>
         <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '14px' }}>
            Quality Checklist
         </Typography>
         <Grid container spacing={1}>
           {[
             { label: "1. The correct size of lift van was measured.", key: "correctSize" },
             { label: "2. The crates build with new ply wood sheets.", key: "plyWood" },
             { label: "3. The crates build with new wood bars.", key: "woodBars" },
             { label: "4. All the parts and joints are well together.", key: "partsWellTogether" },
             { label: "5. Is there any holes/cracks found in the crate?", key: "holesCrakes" },
             { label: "6. If yes, has the holes/cracks filled up with silicon?", key: "holesFilledWithSilicon" },
             { label: "7. The crates fully waterproofed from inside with plastic sheets.", key: "cratesWaterproof" },
             { label: "8. The crates roof top has intact with tin sheet.", key: "cratesRoof" },
             { label: "9 .The signs and shipper details are clearly mentioned on crates", key: "clearlyMentioned" },
             { label: "10. The crates tie-up with steel bindings all around.", key: "tiedUpCrates" },
             { label: "11. The tare weight of the lift van noted.", key: "tareWeightNoted" }
           ].map((item, index) => (
              <Grid item xs={6} key={index}>
                <Typography sx={checklistItemStyle}>
                    <DynamicCheckbox
                        label={item.label}
                        checkId1={`check1_${index}`}
                        checkLabel1="Yes"
                        checkId2={`check2_${index}`}
                        checkLabel2="No"
                        value1={formContent?.[item.key]}
                        value2={formContent?.[item.key]}
                        onChange1={() => {}}
                        onChange2={() => {}}
                        disabled={true}
                    />
                </Typography>
              </Grid>
           ))}
         </Grid>
      </Box>

      {/* Remarks */}
      <Box sx={{ mb: 3 }}>
         {formContent?.carpenterRemarks?.trim() && (
            <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography sx={labelStyle}>Carpenter Remarks:</Typography>
                <Typography sx={valueStyle}>{formContent.carpenterRemarks}</Typography>
            </Box>
         )}
         {formContent?.managerRemarks?.trim() && (
             <Box sx={{ display: 'flex', mb: 1 }}>
                <Typography sx={labelStyle}>Manager Warehouse Remarks:</Typography>
                <Typography sx={valueStyle}>{formContent.managerRemarks}</Typography>
            </Box>
         )}
      </Box>

      {/* Signatures */}
      <Box sx={{ mt: 4, pageBreakInside: 'avoid' }}>
        <Grid container spacing={4}>
          <Grid item xs={6} sx={{ textAlign: 'center' }}>
             <Box sx={{ height: '80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', mb: 1 }}>
                {formContent?.managerWarehouseSign && (
                    <img src={`${Bucket}${formContent.managerWarehouseSign}`} height={70} alt="Manager Warehouse Sign" />
                )}
             </Box>
             <Typography sx={{ borderTop: '1px solid #000', pt: 1, fontWeight: 'bold', fontSize: '12px', width: '80%', margin: '0 auto' }}>
                Manager Warehouse
             </Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'center' }}>
              <Box sx={{ height: '80px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', mb: 1 }}>
                {formContent?.carpenterSign && (
                    <img src={`${Bucket}${formContent.carpenterSign}`} height={70} alt="Carpenter Sign" />
                )}
             </Box>
             <Typography sx={{ borderTop: '1px solid #000', pt: 1, fontWeight: 'bold', fontSize: '12px', width: '80%', margin: '0 auto' }}>
                Carpenter
             </Typography>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default WoodenLiftPageDownload;
